import { useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent } from "react";
import {
  buildPartLandingSupplierRedirect,
  buildPartStructuredData,
  liveOfferMatchesModel,
  safePublicUrl,
  type PartLandingOffer,
  type PartLandingViewModel,
} from "@/lib/part-landing";
import { recordPartAcquisitionEvent } from "@/lib/part-acquisition-analytics";
import ControlExchangeLogo from "@/components/brand/ControlExchangeLogo";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { marketplaceEnabled } from "@/lib/product-focus";
import {
  ensureSourcingSearchStarted,
  getSourcingSearchProof,
} from "@/lib/sourcing-analytics";
import {
  isBlockedMarketScanDestination,
  isBlockedMarketScanSource,
} from "@/lib/market-scan-source-policy";

interface PartLandingPageProps {
  model: PartLandingViewModel;
  enableLiveRefresh?: boolean;
}

type RefreshState = "idle" | "refreshing" | "complete" | "partial" | "failed" | "rate_limited";

function formatPrice(price: number | null, currency: string): string {
  if (price == null || !Number.isFinite(price)) return "Price on request";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency || "GBP",
      maximumFractionDigits: 2,
    }).format(price);
  } catch {
    return `${currency || "GBP"} ${price.toFixed(2)}`;
  }
}

function formatCheckedTime(value: string): string {
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return "Check time unavailable";
  const iso = parsed.toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
}

function formatLifecycle(value: string): string {
  return value.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function evidenceTierLabel(tier: PartLandingViewModel["alternatives"][number]["evidenceTier"]): string {
  if (tier === "manufacturer_confirmed") return "Manufacturer confirmed";
  if (tier === "curated") return "Reviewed cross-reference";
  return "Algorithmically inferred";
}

function isAllowedPartLandingOffer(offer: Pick<PartLandingOffer, "sourceId" | "retailer" | "sellerName" | "productUrl">): boolean {
  return !isBlockedMarketScanSource(offer.sourceId, offer.retailer)
    && !isBlockedMarketScanSource(offer.sourceId, offer.sellerName)
    && !isBlockedMarketScanDestination(offer.productUrl);
}

function toLiveOffer(raw: any, model: PartLandingViewModel): PartLandingOffer | null {
  if (!raw || !liveOfferMatchesModel(raw, model)) return null;
  if (isBlockedMarketScanSource(raw.sourceId, raw.sourceName)
    || isBlockedMarketScanDestination(raw.productUrl)) return null;
  const productUrl = safePublicUrl(raw.productUrl);
  if (!productUrl) return null;
  const observedAt = typeof raw.observedAt === "string" ? raw.observedAt : new Date().toISOString();
  const sourceId = String(raw.sourceId || "supplier");
  const sourceItemKey = String(raw.sourceItemKey || raw.id || productUrl);
  const availability = Number.isFinite(Number(raw.availability)) ? Number(raw.availability) : null;
  return {
    id: String(raw.id || `${sourceId}:${sourceItemKey}`),
    stableKey: `${sourceId}:${sourceItemKey}`,
    sourceId,
    sourceItemKey,
    retailer: String(raw.sourceName || "Supplier"),
    sourceType: sourceId === "oemsecrets" ? "Retailer via data provider" : "Direct supplier source",
    sellerName: null,
    sellerVerified: null,
    partNumber: String(raw.partNumber || model.formattedMpn),
    manufacturer: String(raw.manufacturer || model.manufacturer),
    condition: "Not stated by source",
    price: raw.price == null || !Number.isFinite(Number(raw.price)) ? null : Number(raw.price),
    currency: /^[A-Z]{3}$/.test(String(raw.currency || "")) ? String(raw.currency) : "GBP",
    availability,
    availabilityText: String(raw.availabilityText || (availability == null ? "Stock not stated" : `${availability} reported`)),
    stockConfidence: availability == null ? "low" : raw.evidence === "structured_data" ? "high" : "medium",
    observedAt,
    freshness: raw.freshness === "cached" || raw.freshness === "expired" ? raw.freshness : "fresh",
    productUrl,
    imageUrl: safePublicUrl(raw.imageUrl),
    vatStatus: "VAT treatment not supplied",
    shippingStatus: "Shipping not supplied",
    importStatus: "Import costs not supplied",
    deliveryEstimate: "Confirm with retailer",
    warranty: "Not supplied",
    returns: "Confirm retailer policy",
    costComplete: false,
    isFirstParty: false,
    purchasable: true,
    outboundOfferReference: typeof raw.outboundOfferReference === "string" ? raw.outboundOfferReference : null,
    outboundCapability: typeof raw.outboundCapability === "string" ? raw.outboundCapability : null,
    outboundCapabilityExpiresAt: typeof raw.outboundCapabilityExpiresAt === "string" ? raw.outboundCapabilityExpiresAt : null,
  };
}

function stableMergeOffers(previous: PartLandingOffer[], refreshed: PartLandingOffer[]): PartLandingOffer[] {
  const refreshedByKey = new Map(refreshed.filter(isAllowedPartLandingOffer).map((offer) => [offer.stableKey, offer]));
  const retained = previous.filter(isAllowedPartLandingOffer).map((offer) => {
    const replacement = refreshedByKey.get(offer.stableKey);
    if (!replacement || offer.isFirstParty) return offer;
    refreshedByKey.delete(offer.stableKey);
    return { ...offer, ...replacement };
  });
  // Existing positions never move during hydration refresh. Newly discovered
  // sources are appended deterministically.
  const additions = [...refreshedByKey.values()].sort((left, right) => (
    left.retailer.localeCompare(right.retailer) || left.stableKey.localeCompare(right.stableKey)
  ));
  return [...retained, ...additions];
}

function upsertMeta(attribute: "name" | "property", key: string, value: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = value;
}

function applyClientMetadata(model: PartLandingViewModel) {
  document.title = model.metadata.title;
  upsertMeta("name", "description", model.metadata.description);
  upsertMeta("name", "robots", model.metadata.robots);
  upsertMeta("name", "googlebot", model.metadata.robots);
  upsertMeta("property", "og:title", model.metadata.title);
  upsertMeta("property", "og:description", model.metadata.description);
  upsertMeta("property", "og:url", model.canonicalUrl);

  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }
  canonical.href = model.canonicalUrl;

  let jsonLd = document.head.querySelector<HTMLScriptElement>('script[data-part-landing-jsonld="true"]');
  if (!jsonLd) {
    jsonLd = document.createElement("script");
    jsonLd.type = "application/ld+json";
    jsonLd.dataset.partLandingJsonld = "true";
    document.head.appendChild(jsonLd);
  }
  jsonLd.textContent = JSON.stringify(buildPartStructuredData(model));
}

function OfferCard({
  offer,
  opening,
  onExternalClick,
}: {
  offer: PartLandingOffer;
  opening: boolean;
  onExternalClick: (offer: PartLandingOffer) => void;
}) {
  if (!isAllowedPartLandingOffer(offer)) return null;
  const actionClassName = "inline-flex min-h-9 items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-wait disabled:opacity-60";
  return (
    <article className="border-b border-border py-5 last:border-b-0" data-offer-key={offer.stableKey}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="font-semibold text-foreground">{offer.retailer}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{offer.sourceType} · {offer.condition}{offer.sellerVerified === true ? " · Verified seller" : ""}</p>
        </div>
        <div className="sm:text-right">
          <p className="text-lg font-semibold text-foreground">{formatPrice(offer.price, offer.currency)}</p>
          <p className="text-xs text-muted-foreground">Original advertised currency: {offer.currency}</p>
        </div>
      </div>

      <dl className="mt-4 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <div><dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Reported stock</dt><dd className="mt-1 font-medium">{offer.availabilityText}</dd><dd className="text-xs text-muted-foreground">{formatLifecycle(offer.stockConfidence)} confidence</dd></div>
        <div><dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Checked</dt><dd className="mt-1"><time dateTime={offer.observedAt}>{formatCheckedTime(offer.observedAt)}</time></dd><dd className="text-xs text-muted-foreground">{formatLifecycle(offer.freshness)} observation</dd></div>
        <div><dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Delivered-cost coverage</dt><dd className="mt-1">{offer.costComplete ? "Price, VAT and shipping supplied" : "Incomplete total cost"}</dd><dd className="text-xs text-muted-foreground">{offer.vatStatus}; {offer.shippingStatus}</dd></div>
        <div><dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Delivery</dt><dd className="mt-1">{offer.deliveryEstimate}</dd><dd className="text-xs text-muted-foreground">{offer.importStatus}</dd></div>
      </dl>

      <div className="mt-3 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
        <p><span className="font-medium text-foreground">Warranty:</span> {offer.warranty}</p>
        <p><span className="font-medium text-foreground">Returns:</span> {offer.returns}</p>
      </div>

      <div className="mt-3 flex justify-end">
        {offer.isFirstParty ? (
          <a href={offer.productUrl} className={actionClassName}>
            View this offer
            <span aria-hidden="true" className="ml-1.5">↗</span>
          </a>
        ) : (
          <button
            type="button"
            onClick={() => onExternalClick(offer)}
            disabled={opening}
            className={actionClassName}
            data-analytics="part_landing.supplier_click"
          >
            {opening ? "Preparing secure link…" : "Check price & stock"}
            <span aria-hidden="true" className="ml-1.5">↗</span>
          </button>
        )}
      </div>
    </article>
  );
}

export default function PartLandingPage({ model, enableLiveRefresh = true }: PartLandingPageProps) {
  const [offers, setOffers] = useState<PartLandingOffer[]>(() => model.offers.filter(isAllowedPartLandingOffer));
  const [refreshState, setRefreshState] = useState<RefreshState>("idle");
  const [refreshMessage, setRefreshMessage] = useState("Cached supplier observations shown.");
  const [watchOpen, setWatchOpen] = useState(false);
  const [watchEmail, setWatchEmail] = useState("");
  const [watchCompany, setWatchCompany] = useState("");
  const [watchState, setWatchState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [watchMessage, setWatchMessage] = useState("");
  const watchOpener = useRef<HTMLButtonElement | null>(null);
  const [openingOfferKey, setOpeningOfferKey] = useState<string | null>(null);
  const refreshStarted = useRef(false);
  const refreshOperation = useRef<Promise<PartLandingOffer[]> | null>(null);
  const sourcingSearchId = useRef<string | null>(null);
  const sourcingSearchOperation = useRef<Promise<boolean> | null>(null);
  const openingOffer = useRef(false);
  const exactOffers = useMemo(() => offers.filter((offer) => offer.purchasable), [offers]);

  const acquisitionContext = {
    slug: model.slug,
    manufacturer: model.manufacturer,
    normalizedMpn: model.normalizedMpn,
    surface: "part_landing" as const,
  };
  const fullScanUrl = `/?q=${encodeURIComponent(`${model.manufacturer} ${model.formattedMpn}`)}`;
  const openWatch = (event: MouseEvent<HTMLButtonElement>) => {
    watchOpener.current = event.currentTarget;
    setWatchOpen(true);
  };

  const refreshOffers = (trigger: "web_automatic" | "web_manual"): Promise<PartLandingOffer[]> => {
    if (refreshOperation.current) return refreshOperation.current;
    const operation = (async () => {
      void recordPartAcquisitionEvent({
        eventName: trigger === "web_automatic" ? "part_live_refresh" : "part_live_scan_activation",
        ...acquisitionContext,
        properties: { trigger },
      });
      setRefreshState("refreshing");
      setRefreshMessage("Refreshing live supplier observations…");
      try {
        const response = await fetch("/api/market-scan/refresh", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query: `${model.manufacturer} ${model.formattedMpn}`,
            rawQuery: model.formattedMpn,
            manufacturer: model.manufacturer,
            quantity: 1,
            location: "United Kingdom",
            currency: "GBP",
          }),
        });
        if (response.status === 429) {
          setRefreshState("rate_limited");
          setRefreshMessage("Web-supplier refresh is temporarily rate limited. Cached observations remain available.");
          return [];
        }
        if (!response.ok) throw new Error(`Refresh failed (${response.status})`);
        const payload = await response.json();
        const refreshed = Array.isArray(payload?.offers)
          ? payload.offers.map((offer: any) => toLiveOffer(offer, model)).filter(Boolean) as PartLandingOffer[]
          : [];
        setOffers((current) => stableMergeOffers(current, refreshed));
        const sources = Array.isArray(payload?.sources)
          ? payload.sources.filter((source: any) => !isBlockedMarketScanSource(source?.sourceId, source?.sourceName))
          : [];
        if (sources.length === 0) {
          setRefreshState("failed");
          setRefreshMessage("No web-supplier source was available for this refresh. Cached observations remain visible; this is not a no-stock result.");
          return refreshed;
        }
        const responded = sources.filter((source: any) => ["success", "empty"].includes(source?.status)).length;
        const failures = sources.filter((source: any) => ["error", "timeout", "blocked"].includes(source?.status));
        if (failures.length > 0) {
          setRefreshState("partial");
          setRefreshMessage(`${responded} of ${sources.length} web sources responded. Cached results are retained for failed sources.`);
        } else {
          setRefreshState("complete");
          setRefreshMessage(`${responded} of ${sources.length} web sources responded.`);
        }
        return refreshed;
      } catch {
        setRefreshState("failed");
        setRefreshMessage("Web suppliers could not be reached. Cached observations remain visible; this is not a no-stock result.");
        return [];
      }
    })();
    refreshOperation.current = operation;
    void operation.finally(() => {
      if (refreshOperation.current === operation) refreshOperation.current = null;
    });
    return operation;
  };

  const ensurePartSourcingSearch = (): Promise<boolean> => {
    if (sourcingSearchOperation.current) return sourcingSearchOperation.current;
    if (!sourcingSearchId.current) sourcingSearchId.current = crypto.randomUUID();
    let parentReady = false;
    const operation = ensureSourcingSearchStarted({
      id: sourcingSearchId.current,
      query: `${model.manufacturer} ${model.formattedMpn}`,
      normalizedQuery: model.normalizedMpn,
      quantity: 1,
      deliveryCountry: "United Kingdom",
      displayCurrency: "GBP",
      acceptedConditions: ["new", "used", "refurbished"],
      machineDown: false,
    }, {
      surface: "part_landing",
      canonicalPartPage: true,
      partSlug: model.slug,
      normalizedMpn: model.normalizedMpn,
    }, () => {
      parentReady = true;
    }).then((completed) => completed || parentReady);
    sourcingSearchOperation.current = operation;
    void operation.finally(() => {
      if (sourcingSearchOperation.current === operation) sourcingSearchOperation.current = null;
    });
    return operation;
  };

  const openExternalOffer = async (offer: PartLandingOffer) => {
    if (openingOffer.current) return;
    openingOffer.current = true;
    setOpeningOfferKey(offer.stableKey);
    try {
      const expiresAt = new Date(offer.outboundCapabilityExpiresAt || "").getTime();
      const needsCapability = !offer.outboundCapability
        || !offer.outboundOfferReference
        || !Number.isFinite(expiresAt)
        || expiresAt <= Date.now() + 5_000;
      const [searchReady, refreshed] = await Promise.all([
        ensurePartSourcingSearch(),
        needsCapability ? refreshOffers("web_manual") : Promise.resolve([]),
      ]);
      const candidate = needsCapability
        ? refreshed.find((item) => item.stableKey === offer.stableKey) ?? offer
        : offer;
      const searchId = sourcingSearchId.current;
      const redirect = searchReady ? buildPartLandingSupplierRedirect({
        offer: candidate,
        searchId,
        searchProof: getSourcingSearchProof(searchId),
        partSlug: model.slug,
        normalizedMpn: model.normalizedMpn,
        rankPosition: Math.max(1, exactOffers.findIndex((item) => item.stableKey === offer.stableKey) + 1),
        resultCount: exactOffers.length,
      }) : null;
      if (redirect) {
        window.location.assign(redirect);
        return;
      }
      setRefreshMessage("We could not prepare a tracked retailer link, so we are opening the full Market Scan instead.");
      window.location.assign(fullScanUrl);
    } catch {
      setRefreshMessage("We could not prepare a tracked retailer link, so we are opening the full Market Scan instead.");
      window.location.assign(fullScanUrl);
    } finally {
      openingOffer.current = false;
      setOpeningOfferKey(null);
    }
  };

  const saveWatch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setWatchState("saving");
    setWatchMessage("");
    try {
      const response = await fetch("/api/part-watch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: watchEmail,
          manufacturer: model.manufacturer,
          partNumber: model.formattedMpn,
          company: watchCompany,
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error?.message || "The watch could not be saved.");
      setWatchState("saved");
      setWatchMessage(`We’ll email ${watchEmail.trim()} when price or stock changes.`);
    } catch (error) {
      setWatchState("error");
      setWatchMessage(error instanceof Error ? error.message : "The watch could not be saved.");
    }
  };

  useEffect(() => {
    applyClientMetadata(model);
    void recordPartAcquisitionEvent({
      eventName: "part_landing_impression",
      ...acquisitionContext,
    });
  // One impression per canonical model mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  useEffect(() => {
    if (!enableLiveRefresh || refreshStarted.current) return;
    refreshStarted.current = true;
    void refreshOffers("web_automatic");
  // The canonical identity is immutable for the lifetime of this hydrated page.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableLiveRefresh, model.slug]);

  const encodedMpn = encodeURIComponent(model.formattedMpn);
  const encodedManufacturer = encodeURIComponent(model.manufacturer);
  const rfqUrl = marketplaceEnabled
    ? `/requests/new?part_number=${encodedMpn}&manufacturer=${encodedManufacturer}&quantity=1&condition=any&delivery_location=${encodeURIComponent("United Kingdom")}`
    : `/sourcing-help?${new URLSearchParams({
      partNumber: model.formattedMpn,
      manufacturer: model.manufacturer,
      quantity: "1",
      deliveryLocation: "United Kingdom",
    }).toString()}`;

  return (
    <div className="min-h-screen bg-background text-foreground" data-part-landing-version={model.version} data-indexable={String(model.eligibility.indexable)}>
      <header className="border-b bg-background">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <a href="/" aria-label="Control Exchange home" className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <ControlExchangeLogo markClassName="h-7" wordmarkClassName="hidden text-sm sm:block" />
          </a>
          <nav aria-label="Primary" className="flex items-center gap-4 text-sm">
            <a href="/" className="hover:text-primary">Market Scan</a>
            <a href="/manufacturers" className="hidden hover:text-primary sm:inline">Manufacturers</a>
            <a href="/research" className="hover:text-primary">Part research</a>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:py-10">
        <nav aria-label="Breadcrumb" className="mb-5 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-2">
            <li><a href="/" className="hover:text-primary">Home</a></li><li aria-hidden="true">/</li>
            <li><a href={model.internalLinks.find((link) => link.kind === "manufacturer")?.url || "/manufacturers"} className="hover:text-primary">{model.manufacturer}</a></li><li aria-hidden="true">/</li>
            <li aria-current="page" className="font-mono text-foreground">{model.formattedMpn}</li>
          </ol>
        </nav>

        <section className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_260px] lg:items-start">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{[model.categoryLabel, model.lifecycle ? `Lifecycle: ${formatLifecycle(model.lifecycle.status)}` : null, model.eligibility.identitySupported ? "Identity checked" : "Identity pending review"].filter(Boolean).join(" · ")}</p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {model.manufacturer} <span className="font-mono">{model.formattedMpn}</span> price, stock &amp; alternatives
            </h1>
            <p className="mt-3 max-w-3xl text-base leading-7 text-muted-foreground">{model.description}</p>
            <dl className="mt-6 grid max-w-3xl gap-4 border-t pt-4 text-sm sm:grid-cols-3">
              <div><dt className="text-xs uppercase tracking-wide text-muted-foreground">Manufacturer</dt><dd className="mt-1 font-medium">{model.manufacturer}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-muted-foreground">MPN</dt><dd className="mt-1 break-all font-mono font-medium">{model.formattedMpn}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-muted-foreground">Model / family</dt><dd className="mt-1 font-medium">{model.family || model.model || "Not stated"}</dd></div>
            </dl>
          </div>
          <aside className="lg:border-l lg:pl-6">
            <h2 className="text-sm font-semibold">Procurement actions</h2>
            <div className="mt-3 grid gap-2">
              <a href={fullScanUrl} onClick={() => void recordPartAcquisitionEvent({ eventName: "part_live_scan_activation", ...acquisitionContext, properties: { trigger: "full_market_scan" } })} className="inline-flex min-h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Run full Market Scan</a>
              <button type="button" onClick={() => void refreshOffers("web_manual")} disabled={refreshState === "refreshing"} className="min-h-10 rounded-md border px-4 py-2 text-sm font-medium disabled:opacity-60">
                {refreshState === "refreshing" ? "Refreshing web suppliers…" : "Refresh web suppliers"}
              </button>
              <button type="button" onClick={openWatch} className="min-h-10 rounded-md border px-4 py-2 text-center text-sm font-medium hover:bg-muted">Watch price / stock</button>
              <a href={rfqUrl} onClick={() => void recordPartAcquisitionEvent({ eventName: "rfq", ...acquisitionContext })} className="min-h-10 rounded-md border px-4 py-2 text-center text-sm font-medium hover:bg-muted">Request a quote</a>
            </div>
            <p className="mt-3 text-xs leading-5 text-muted-foreground" aria-live="polite">{refreshMessage}</p>
          </aside>
        </section>

        <section className="mt-12 border-t pt-8" aria-labelledby="exact-offers-heading">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 id="exact-offers-heading" className="text-2xl font-semibold">Exact offers</h2>
              <p className="mt-1 text-sm text-muted-foreground">Only offers matching the canonical manufacturer and MPN are shown here.</p>
              {exactOffers.some((offer) => !offer.isFirstParty) && <p className="mt-1 text-xs text-muted-foreground">Affiliate disclosure: Control Exchange may earn a commission if you visit or buy from a retailer. Payment does not change organic offer ranking.</p>}
            </div>
            <p className="text-sm font-medium">{exactOffers.length} exact offer{exactOffers.length === 1 ? "" : "s"}</p>
          </div>
          {exactOffers.length > 0 ? (
            <div className="mt-3">
              {exactOffers.map((offer) => (
                <OfferCard
                  key={offer.stableKey}
                  offer={offer}
                  opening={openingOfferKey === offer.stableKey}
                  onExternalClick={(selectedOffer) => void openExternalOffer(selectedOffer)}
                />
              ))}
            </div>
          ) : (
            <div className="mt-4 border-t py-5">
              <h3 className="font-semibold">No verified exact offer is cached</h3>
              <p className="mt-1 text-sm text-muted-foreground">This does not mean the part is unavailable. Refresh live sources, compare an evidence-ranked alternative, watch stock, or send an RFQ.</p>
              <div className="mt-4 flex flex-wrap gap-2"><button type="button" onClick={openWatch} className="rounded-md border px-3 py-2 text-sm font-semibold">Watch stock</button><a href={rfqUrl} onClick={() => void recordPartAcquisitionEvent({ eventName: "rfq", ...acquisitionContext })} className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground">Send RFQ</a></div>
            </div>
          )}
        </section>

        <section className="mt-12 border-t pt-8" aria-labelledby="alternatives-heading">
          <h2 id="alternatives-heading" className="text-2xl font-semibold">Alternatives and successors</h2>
          <p className="mt-1 text-sm text-muted-foreground">Ordered by manufacturer confirmation, reviewed cross-reference, then inferred catalogue variants.</p>
          {model.alternatives.length > 0 ? (
            <div className="mt-4 divide-y">
              {model.alternatives.map((alternative) => (
                <article key={alternative.id} className="py-5 first:pt-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{evidenceTierLabel(alternative.evidenceTier)}</p><h3 className="mt-1 font-semibold">{alternative.manufacturer} <span className="font-mono">{alternative.partNumber}</span></h3></div>
                    <span className="text-xs text-muted-foreground">{Math.round(alternative.confidence * 100)}% evidence confidence</span>
                  </div>
                  <p className="mt-3 text-sm"><span className="font-medium">Relationship:</span> {formatLifecycle(alternative.relationship)}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{alternative.fitNotes || "No fit notes supplied."}</p>
                  {alternative.evidenceTier === "algorithmic" && <p className="mt-3 border-l-2 border-amber-500 pl-3 text-sm font-medium text-amber-900">Compatibility not confirmed—engineering review required.</p>}
                  {alternative.requiresChanges && alternative.evidenceTier !== "algorithmic" && <p className="mt-3 text-sm font-medium text-amber-800">Changes or engineering review may be required.</p>}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <a href={`/?q=${encodeURIComponent(`${alternative.manufacturer} ${alternative.partNumber}`.trim())}`} onClick={() => void recordPartAcquisitionEvent({ eventName: "alternative_comparison", ...acquisitionContext, properties: { alternative_mpn: alternative.partNumber, alternative_manufacturer: alternative.manufacturer, relationship: alternative.relationship } })} className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground">Compare this MPN</a>
                    {alternative.evidenceUrl && <a href={alternative.evidenceUrl} target="_blank" rel="noopener noreferrer" className="rounded-lg border px-3 py-2 text-sm font-semibold">View evidence</a>}
                    {alternative.canonicalUrl && <a href={alternative.canonicalUrl} className="rounded-lg border px-3 py-2 text-sm font-semibold">Part page</a>}
                  </div>
                </article>
              ))}
            </div>
          ) : <p className="mt-4 border-t py-5 text-sm text-muted-foreground">No evidence-backed alternatives have been recorded yet.</p>}
        </section>

        <div className="mt-12 grid gap-10 border-t pt-8 lg:grid-cols-2 lg:divide-x">
          <section aria-labelledby="specifications-heading">
            <h2 id="specifications-heading" className="text-xl font-semibold">Technical specifications</h2>
            {model.specifications.length > 0 ? <dl className="mt-4 divide-y">{model.specifications.map((specification) => <div key={`${specification.name}:${specification.value}`} className="grid gap-1 py-3 sm:grid-cols-[160px_1fr]"><dt className="text-sm font-medium text-muted-foreground">{specification.name}</dt><dd className="text-sm">{specification.value}</dd></div>)}</dl> : <p className="mt-3 text-sm text-muted-foreground">Substantive technical specifications have not yet been verified.</p>}
          </section>
          <section className="lg:pl-10" aria-labelledby="evidence-heading">
            <h2 id="evidence-heading" className="text-xl font-semibold">Research and evidence</h2>
            {model.researchSummary && <p className="mt-3 text-sm leading-6 text-muted-foreground">{model.researchSummary}</p>}
            {model.evidenceLinks.length > 0 ? <ul className="mt-4 space-y-2">{model.evidenceLinks.map((link) => <li key={link.url}><a href={link.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary underline-offset-4 hover:underline">{link.label} ↗</a></li>)}</ul> : <p className="mt-3 text-sm text-muted-foreground">No official evidence link is available yet.</p>}
          </section>
        </div>

        <section className="mt-10 border-t pt-6 text-sm leading-6">
          <h2 className="font-semibold">UK and European delivery caveats</h2>
          <p className="mt-1 text-muted-foreground">External advertised prices may exclude VAT, freight, customs clearance and import duties. Confirm the delivered cost, destination availability, Incoterms and required delivery date with the retailer before ordering. GBP/EUR conversions are indicative when shown; the retailer charges in its original currency.</p>
        </section>

        {model.internalLinks.length > 0 && <nav className="mt-10 border-t pt-6" aria-label="Related part discovery"><h2 className="text-lg font-semibold">Explore related parts</h2><ul className="mt-3 flex flex-wrap gap-x-5 gap-y-2">{model.internalLinks.map((link) => <li key={`${link.kind}:${link.url}`}><a href={link.url} className="text-sm font-medium text-primary underline-offset-4 hover:underline">{link.label}</a></li>)}</ul></nav>}
      </main>

      <footer className="mt-12 border-t"><div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-6 text-sm text-muted-foreground sm:flex-row sm:justify-between sm:px-6"><p>Control Exchange — industrial automation procurement intelligence.</p><p><a href="/terms" className="hover:text-primary">Terms</a> · <a href="/privacy" className="hover:text-primary">Privacy</a></p></div></footer>

      {watchOpen && (
        <Dialog open={watchOpen} onOpenChange={setWatchOpen}>
          <DialogContent className="max-w-md rounded-xl bg-card p-5" onCloseAutoFocus={(event) => {
            event.preventDefault();
            watchOpener.current?.focus();
          }}>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Watch {model.formattedMpn}</DialogTitle>
              <DialogDescription>Get an email when a new exact offer, price change or stock observation is recorded.</DialogDescription>
            </DialogHeader>
            {watchState === "saved" ? (
              <div className="mt-5 rounded-lg bg-emerald-50 p-4 text-sm font-medium text-emerald-900" role="status">{watchMessage}</div>
            ) : (
              <form className="mt-5 space-y-4" onSubmit={saveWatch}>
                <div><label htmlFor="part-watch-email" className="text-sm font-medium">Email address</label><input id="part-watch-email" type="email" autoComplete="email" required value={watchEmail} onChange={(event) => setWatchEmail(event.target.value)} placeholder="buyer@company.com" className="mt-1 min-h-11 w-full rounded-lg border bg-background px-3 text-sm" /></div>
                <input type="text" name="company" tabIndex={-1} autoComplete="off" value={watchCompany} onChange={(event) => setWatchCompany(event.target.value)} className="hidden" aria-hidden="true" />
                {watchMessage && <p className="text-sm text-destructive" role="alert">{watchMessage}</p>}
                <button type="submit" disabled={watchState === "saving"} className="min-h-11 w-full rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">{watchState === "saving" ? "Saving watch…" : "Watch price and stock"}</button>
                <p className="text-xs leading-5 text-muted-foreground">We use this email only for this procurement watch. You can unsubscribe from any alert.</p>
              </form>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
